// src/components/AttendanceEditPane.jsx
// 出欠の修正（先生・教務）【2026-10-07】
//
//   先生から「手動で出席・遅刻を直すところが無い」。受講者名簿（学生管理）の中には手動出席の欄が
//   あったが、先生のメニューからは届かなかった。授業管理の下に専用のページとして置く。
//     ・学生 × 回 の表。マスを押すと、上の欄で 出席／遅刻／早退／欠席／公欠 を選んで直せる
//     ・直すのは POST /api/attendance/manual（アプリの手動出席と同じ。誰が直したかが記録に残る）
//     ・表に居ない学生・今日の回は、上の「手動で出席を記録」から学籍番号で入れる（2026-10-13 上へ移した）
//   直したマスはその場で書き換える（画面全体を読み直すと表のスクロール位置が戻るため）。
//   【2026-10-07】まとめて直せるようにした（Excel と同じ選び方）
//     ・クリック … そのマスだけ
//     ・Ctrl（Mac は ⌘）＋クリック … 足す／外す
//     ・Shift＋クリック … 最後に押したマスからの四角い範囲（Ctrl＋Shift で今の選択に足す）
//     ・Esc … 選択をやめる
//   【2026-10-07】「出欠の確認」（学生からの申請・確認待ちの打刻）と1つの画面にした
//     ・上に「この授業で対応が必要なもの」を件数つきで出し、その場で認める・確定する
//     ・申請や確認待ちがあるマスには印を付ける。マスを選ぶと、その申請の中身が出て、そこから開ける
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MARKS, ABSENT } from './ClassRosterPane';
import ManualAttendancePanel from './ManualAttendancePanel';
import AttendanceRequestsPane from './AttendanceRequestsPane';
import PendingAttendancePane from './PendingAttendancePane';

const CHOICES = [
  ['present', '出席'], ['late', '遅刻'], ['early_leave', '早退'], ['absent', '欠席'], ['excused', '公欠'],
];
const IRREGULAR = { mark: '?', color: '#b45309', label: '要確認' };
const markOf = (st) => (st === 'irregular' ? IRREGULAR : (MARKS[st] || ABSENT));
const md = (d) => { const s = String(d || '').slice(0, 10).split('-'); return s.length === 3 ? `${Number(s[1])}/${Number(s[2])}` : ''; };

export default function AttendanceEditPane({ classId, auth, reload, setErr, setMsg, sessions = [], students = [], cls = null }) {
  const [query, setQuery]   = useState('');
  const [onlyEnrolled, setOnlyEnrolled] = useState(false);
  const [sel, setSel]       = useState([]);     // 選んだマス [{ stu, ses }]
  const [anchor, setAnchor] = useState(null);   // Shift の起点 { r, c }（表の行・列の番号）
  const [progress, setProgress] = useState(null);   // まとめて直している途中 { done, total }
  const [reason, setReason] = useState('');
  const [busy, setBusy]     = useState(false);
  const [over, setOver]     = useState({});     // `${userId}:${sessionId}` → 直したあとの状態
  const readOnly = !!(cls && cls.closure_id);

  // ── 対応が必要なもの（学生からの申請・確認待ちの打刻）──
  const [todo, setTodo]       = useState({ requests: [], pending: [], loaded: false });
  const [todoTab, setTodoTab] = useState(null);     // null（閉じている）/ 'requests' / 'pending'
  const [focus, setFocus]     = useState(null);     // 開いておく申請 { id, n }
  const todoRef = useRef(null);
  const loadTodo = async () => {
    try {
      const h = { headers: auth() };
      const [a, b] = await Promise.all([
        fetch('/api/attendance/requests/inbox?status=open&classId=' + encodeURIComponent(classId), h).then(r => r.json()).catch(() => ({})),
        fetch('/api/attendance/pending?classId=' + encodeURIComponent(classId), h).then(r => r.json()).catch(() => ({})),
      ]);
      setTodo({ requests: (a && a.requests) || [], pending: (b && b.items) || [], loaded: true });
    } catch (e) { setTodo(t => ({ ...t, loaded: true })); }
  };
  useEffect(() => { loadTodo(); }, [classId]); // eslint-disable-line react-hooks/exhaustive-deps
  const reqAt = useMemo(() => {
    const m = new Map();
    for (const r of todo.requests) if (r.userId && r.sessionId) m.set(r.userId + ':' + r.sessionId, r);
    return m;
  }, [todo.requests]);
  const pendAt = useMemo(() => {
    const m = new Map();
    for (const x of todo.pending) if (x.userId && x.sessionId) m.set(x.userId + ':' + x.sessionId, x);
    return m;
  }, [todo.pending]);
  const openTodo = (tab, reqId) => {
    setTodoTab(tab);
    if (reqId) setFocus({ id: reqId, n: Date.now() });
    setTimeout(() => { if (todoRef.current) todoRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 50);
  };
  // 申請を認めた・打刻を確定したら、件数と表を読み直す（表の中身が変わるため）
  const afterDecide = () => { loadTodo(); setOver({}); reload(); };

  const cellOf = (stu, ses) => {
    const k = stu.id + ':' + ses.id;
    return Object.prototype.hasOwnProperty.call(over, k) ? over[k] : (stu.cells || {})[ses.id];
  };

  const list = useMemo(() => {
    const qq = query.trim().toLowerCase();
    return students.filter(s => (!onlyEnrolled || !s.is_outside_roster)
      && (!qq || String(s.student_id || '').toLowerCase().includes(qq) || String(s.name || '').toLowerCase().includes(qq)));
  }, [students, query, onlyEnrolled]);

  const keyOf = (stu, ses) => stu.id + ':' + ses.id;
  const isSel = (stu, ses) => sel.some(x => x.stu.id === stu.id && x.ses.id === ses.id);
  const clear = () => { setSel([]); setReason(''); };
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') clear(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // マスを押したとき（Ctrl/⌘ で足す・外す、Shift で範囲）
  const onCell = (e, r, c) => {
    const stu = list[r], ses = sessions[c];
    const multi = e.ctrlKey || e.metaKey;
    if (e.shiftKey && anchor) {
      const r0 = Math.min(anchor.r, r), r1 = Math.max(anchor.r, r);
      const c0 = Math.min(anchor.c, c), c1 = Math.max(anchor.c, c);
      const range = [];
      for (let i = r0; i <= r1; i++) for (let j = c0; j <= c1; j++) if (list[i] && sessions[j]) range.push({ stu: list[i], ses: sessions[j] });
      if (multi) {
        const have = new Set(sel.map(x => keyOf(x.stu, x.ses)));
        setSel(sel.concat(range.filter(x => !have.has(keyOf(x.stu, x.ses)))));
      } else {
        setSel(range);
      }
      return;   // 起点は動かさない（続けて Shift で範囲を変えられる）
    }
    if (multi) {
      setSel(isSel(stu, ses) ? sel.filter(x => !(x.stu.id === stu.id && x.ses.id === ses.id)) : sel.concat([{ stu, ses }]));
    } else {
      setSel([{ stu, ses }]);
      setReason('');
    }
    setAnchor({ r, c });
  };

  const post = (stu, ses, status, enroll) => fetch('/api/attendance/manual', {
    method: 'POST', headers: auth(),
    body: JSON.stringify({ sessionId: ses.id, userId: stu.id, status, reason: reason.trim() || undefined, ...(enroll ? { enroll: true } : {}) }),
  }).then(async res => ({ res, d: await res.json().catch(() => ({})) }));

  const save = async (status) => {
    const targets = sel.filter(x => cellOf(x.stu, x.ses) !== status);   // もうその状態のマスは送らない
    if (!targets.length) { clear(); return; }
    const label = (CHOICES.find(c => c[0] === status) || [])[1] || status;
    if (targets.length >= 2 && !window.confirm(`${targets.length} マスを「${label}」にします。よろしいですか？`)) return;
    setBusy(true); setErr(''); setMsg('');
    const done = {}; const failed = []; const notEnrolled = [];
    try {
      // 4件ずつ並べて送る（多すぎるとサーバーの接続を食うため）
      for (let i = 0; i < targets.length; i += 4) {
        setProgress({ done: i, total: targets.length });
        const chunk = targets.slice(i, i + 4);
        const rs = await Promise.all(chunk.map(x => post(x.stu, x.ses, status, false).then(r => ({ x, ...r }))));
        for (const { x, res, d } of rs) {
          if (res.ok && d.success) done[keyOf(x.stu, x.ses)] = status;
          else if (res.status === 400 && d.code === 'NOT_ENROLLED') notEnrolled.push(x);
          else failed.push({ x, message: d.message || ('HTTP ' + res.status) });
        }
      }
      // 名簿にいない学生は、まとめて1回だけ聞く
      const people = [...new Map(notEnrolled.map(x => [x.stu.id, x.stu])).values()];
      if (people.length && window.confirm(
        `${people.map(p => p.name || p.student_id).join('、')} さんは履修者名簿にいません。履修者に加えて記録しますか？`)) {
        for (const x of notEnrolled) {
          const { res, d } = await post(x.stu, x.ses, status, true);
          if (res.ok && d.success) done[keyOf(x.stu, x.ses)] = status;
          else failed.push({ x, message: d.message || ('HTTP ' + res.status) });
        }
      }
      setOver(o => ({ ...o, ...done }));
      if (Object.keys(done).length) loadTodo();   // 確認待ちの打刻を直したときは件数が減る
      const n = Object.keys(done).length;
      if (n === 1 && targets.length === 1) {
        const { stu, ses } = targets[0];
        setMsg(`${stu.name || stu.student_id} さんの第${ses.no}回（${md(ses.date)}）を「${label}」にしました`);
      } else if (n > 0) {
        setMsg(`${n} マスを「${label}」にしました`);
      }
      if (failed.length) {
        setErr(`${failed.length} マスは直せませんでした（${failed[0].x.stu.name || failed[0].x.stu.student_id}・第${failed[0].x.ses.no}回：${failed[0].message}${failed.length > 1 ? ' ほか' : ''}）`);
        setSel(failed.map(f => f.x));   // 直せなかったマスを選んだまま残す
      } else {
        clear();
      }
    } finally { setBusy(false); setProgress(null); }
  };

  const one = sel.length === 1 ? sel[0] : null;
  const now = one ? cellOf(one.stu, one.ses) : null;
  const oneReq  = one ? reqAt.get(one.stu.id + ':' + one.ses.id) : null;
  const onePend = one ? pendAt.get(one.stu.id + ':' + one.ses.id) : null;
  const nReq = todo.requests.length, nPend = todo.pending.length;
  const pick = sel.length > 0;

  return (
    <>
      {/* 手動で出席を記録（打刻できなかった学生）。いちばんよく使うので一番上に【2026-10-13】 */}
      {!readOnly && (
        <ManualAttendancePanel classId={classId} sessions={sessions} students={students}
                               auth={auth} reload={reload} setErr={setErr} setMsg={setMsg} defaultOpen />
      )}

      {/* ── この授業で対応が必要なもの ── */}
      <div className="card" ref={todoRef}
           style={{ borderLeft: `4px solid ${nReq + nPend > 0 ? '#f59e0b' : '#22c55e'}`, padding: '0.7rem 1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: '1rem' }}>対応が必要なもの</strong>
          {!todo.loaded ? <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>読み込み中…</span>
            : nReq + nPend === 0 ? <span style={{ color: '#15803d', fontSize: '0.88rem' }}>ありません</span> : null}
          {[['requests', '学生からの申請', nReq, '#b45309'], ['pending', '確認待ちの打刻', nPend, '#7c3aed']].map(([k, l, n, c]) => (
            <button key={k} type="button" onClick={() => setTodoTab(todoTab === k ? null : k)}
                    className={todoTab === k ? 'btn-primary' : 'btn-secondary'}
                    style={{ padding: '0.2rem 0.7rem', fontSize: '0.85rem' }}>
              {l} <span style={{ fontWeight: 700, color: todoTab === k ? undefined : (n > 0 ? c : '#9ca3af') }}>{n}件</span>
              {todoTab === k ? ' ▴' : ' ▾'}
            </button>
          ))}
          <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>
            学生がアプリから出した訂正・公欠の申請と、名簿外・時間外で「確認待ち」になった打刻です。下の表では印が付きます
          </span>
        </div>
        {todoTab === 'requests' && (
          <div style={{ marginTop: '0.6rem' }}>
            <AttendanceRequestsPane classId={classId} focus={focus} onChanged={afterDecide} />
          </div>
        )}
        {todoTab === 'pending' && (
          <div style={{ marginTop: '0.6rem' }}>
            <PendingAttendancePane classId={classId} onChanged={afterDecide} />
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, flex: 1 }}>出欠の表（マスを押して直す）</h2>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="学籍番号・氏名で絞る"
                 style={{ width: 200 }} />
          <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <input type="checkbox" checked={onlyEnrolled} onChange={e => setOnlyEnrolled(e.target.checked)} />
            履修者だけ
          </label>
          <button type="button" className="btn-secondary" style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
                  onClick={() => { setOver({}); reload(); }}>↻ 読み直す</button>
        </div>

        {readOnly ? (
          <p style={{ color: '#1d4ed8', fontSize: '0.88rem' }}>この授業は年度の締めが済んでいるため、出欠は直せません（見るだけ）。</p>
        ) : (
          <p style={{ fontSize: '0.85rem', color: '#4b5563', marginTop: 0 }}>
            マスを押すと、その学生のその回の出欠を直せます。Ctrl（Mac は ⌘）＋クリックで足す、Shift＋クリックで範囲を選んで、まとめて直せます。
            直した記録は「手動」になり、誰がいつ直したかが残ります。
            公欠にした回は、その学生の出席率の分母に入りません。
          </p>
        )}

        {/* 選んだマスを直す欄（表の上に固定） */}
        {pick && !readOnly && (
          <div style={{ position: 'sticky', top: 0, zIndex: 5, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
                        padding: '0.6rem 0.8rem', marginBottom: '0.6rem' }}>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.4rem' }}>
              {one ? (
                <>
                  <strong>{one.stu.name || '（氏名なし）'}</strong>（{one.stu.student_id}）・第{one.ses.no}回（{md(one.ses.date)}）
                  <span style={{ marginLeft: '0.5rem', color: '#4b5563' }}>
                    いま：<span style={{ color: markOf(now).color, fontWeight: 700 }}>{markOf(now).mark} {now ? markOf(now).label : '記録なし（欠席）'}</span>
                  </span>
                </>
              ) : (
                <>
                  <strong>{sel.length} マス</strong>を選択中
                  <span style={{ marginLeft: '0.5rem', color: '#4b5563', fontSize: '0.82rem' }}>
                    （{new Set(sel.map(x => x.stu.id)).size}人 × {new Set(sel.map(x => x.ses.id)).size}回）
                  </span>
                </>
              )}
              {progress && <span style={{ marginLeft: '0.6rem', color: '#1d4ed8' }}>直しています… {progress.done}/{progress.total}</span>}
            </div>
            {oneReq && (
              <div style={{ fontSize: '0.85rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
                            padding: '0.35rem 0.6rem', marginBottom: '0.4rem' }}>
                📩 学生からの申請：<strong>{oneReq.typeLabel}</strong>（{oneReq.previousStatusLabel} → {oneReq.requestedStatusLabel}・{oneReq.categoryLabel}・{oneReq.statusLabel}）
                <span style={{ color: '#4b5563' }}>「{String(oneReq.reason || '').slice(0, 60)}{String(oneReq.reason || '').length > 60 ? '…' : ''}」</span>
                <button type="button" className="btn-secondary" style={{ marginLeft: '0.5rem', padding: '0.1rem 0.6rem', fontSize: '0.8rem' }}
                        onClick={() => openTodo('requests', oneReq.id)}>申請を確認する（認める・認めない）</button>
              </div>
            )}
            {onePend && (
              <div style={{ fontSize: '0.85rem', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6,
                            padding: '0.35rem 0.6rem', marginBottom: '0.4rem' }}>
                ⏳ 確認待ちの打刻：{onePend.reasonLabel}。下のボタンで直すと、その状態で確定します
                <button type="button" className="btn-secondary" style={{ marginLeft: '0.5rem', padding: '0.1rem 0.6rem', fontSize: '0.8rem' }}
                        onClick={() => openTodo('pending')}>確認待ちの一覧を開く</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {CHOICES.map(([v, l]) => {
                const m = v === 'absent' ? ABSENT : MARKS[v];
                return (
                  <button key={v} type="button" disabled={busy || (one && now === v)} onClick={() => save(v)}
                          className={one && now === v ? 'btn-secondary' : 'btn-primary'}
                          style={{ padding: '0.3rem 0.8rem', ...(one && now === v ? {} : { background: m.color, borderColor: m.color }) }}>
                    {m.mark} {l}
                  </button>
                );
              })}
              <input value={reason} onChange={e => setReason(e.target.value)} placeholder="理由（任意・記録に残る）"
                     style={{ flex: '1 1 180px', minWidth: 160 }} maxLength={200} />
              <button type="button" className="btn-secondary" disabled={busy} onClick={clear}>やめる（Esc）</button>
            </div>
          </div>
        )}

        {sessions.length === 0 ? (
          <p style={{ color: '#6b7280' }}>まだ実施した回がありません。今日の回は上の「手動で出席を記録」から入れられます。</p>
        ) : list.length === 0 ? (
          <p style={{ color: '#6b7280' }}>表示する学生がいません。</p>
        ) : (
          <div className="table-wrap" style={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
            {/* Shift＋クリックで文字が選択されて青くならないように userSelect を切る */}
            <table className="data-table" style={{ fontSize: '0.85rem', userSelect: 'none' }}>
              <thead>
                <tr>
                  <th style={{ position: 'sticky', left: 0, top: 0, zIndex: 3, background: '#f9fafb', minWidth: 96 }}>学籍番号</th>
                  <th style={{ position: 'sticky', left: 96, top: 0, zIndex: 3, background: '#f9fafb', minWidth: 110 }}>氏名</th>
                  {sessions.map(s => (
                    <th key={s.id} style={{ position: 'sticky', top: 0, zIndex: 2, background: '#f9fafb', textAlign: 'center',
                                            whiteSpace: 'nowrap', padding: '0.4rem 0.3rem' }} title={String(s.date).slice(0, 10)}>
                      第{s.no}回<div style={{ fontWeight: 400, fontSize: '0.75rem', color: '#6b7280' }}>{md(s.date)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((stu, r) => (
                  <tr key={stu.id}>
                    <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, whiteSpace: 'nowrap' }}>{stu.student_id}</td>
                    <td style={{ position: 'sticky', left: 96, background: '#fff', zIndex: 1, whiteSpace: 'nowrap',
                                 color: stu.is_outside_roster ? '#92400e' : undefined }}
                        title={stu.is_outside_roster ? '履修者名簿にいない学生（打刻のみ）' : undefined}>
                      {stu.name || '—'}{stu.is_outside_roster ? ' ＊' : ''}
                    </td>
                    {sessions.map((ses, c) => {
                      const st = cellOf(stu, ses);
                      const m = markOf(st);
                      const chosen = isSel(stu, ses);
                      const changed = Object.prototype.hasOwnProperty.call(over, stu.id + ':' + ses.id);
                      return (
                        <td key={ses.id} style={{ textAlign: 'center', padding: 0 }}>
                          <button type="button" disabled={readOnly}
                                  onClick={(e) => onCell(e, r, c)}
                                  title={`${stu.name || stu.student_id}・第${ses.no}回：${st ? m.label : '記録なし（欠席）'}`
                                         + (reqAt.has(stu.id + ':' + ses.id) ? '\n📩 学生からの申請あり' : '')
                                         + (pendAt.has(stu.id + ':' + ses.id) ? '\n⏳ 確認待ちの打刻' : '')}
                                  style={{ position: 'relative', width: '100%', minWidth: 38, padding: '0.35rem 0', border: 'none', cursor: readOnly ? 'default' : 'pointer',
                                           background: chosen ? '#dbeafe' : changed ? '#fefce8' : 'transparent',
                                           color: m.color, fontWeight: 700, fontSize: '0.95rem',
                                           outline: chosen ? '2px solid #2563eb' : 'none' }}>
                            {m.mark}
                            {/* 申請・確認待ちの印（右上の点）【2026-10-07】 */}
                            {reqAt.has(stu.id + ':' + ses.id) && (
                              <span style={{ position: 'absolute', top: 2, right: 3, width: 7, height: 7, borderRadius: 999, background: '#f59e0b' }} />
                            )}
                            {pendAt.has(stu.id + ':' + ses.id) && (
                              <span style={{ position: 'absolute', top: 2, left: 3, width: 7, height: 7, borderRadius: 999, background: '#8b5cf6' }} />
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', fontSize: '0.78rem', color: '#4b5563', marginTop: '0.4rem' }}>
          {[MARKS.present, MARKS.late, MARKS.early_leave, ABSENT, MARKS.excused, IRREGULAR].map(m => (
            <span key={m.label}><span style={{ color: m.color, fontWeight: 700 }}>{m.mark}</span> {m.label}</span>
          ))}
          <span><span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 999, background: '#f59e0b' }} /> 学生からの申請あり</span>
          <span><span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 999, background: '#8b5cf6' }} /> 確認待ちの打刻</span>
          <span>＊ 履修者名簿にいない学生</span>
          <span style={{ background: '#fefce8', padding: '0 0.3rem' }}>黄色 ＝ この画面で直したマス</span>
        </div>
      </div>

    </>
  );
}
